import * as React from "react";
import { parseInlineFormatting } from "../../utils/parseInlineFormatting";
import { unwrapIdlessMentionTags } from "../../utils/wrapMentionsInBody";
import { isSameUserId } from "../../utils/signoffAccess";
import { useCollaborateStore } from "../../store/useCollaborateStore";

export type MentionBodyProps = {
  text: string;
  html?: string;
  versionNumber?: number;
};

const TOKEN_RE =
  /<signoff(?:\s+id="([^"]*)")?\s*>@?([^<]*)<\/signoff>|<playbook>@?([^<]*)<\/playbook>|<mention\s+id="([^"]*)"\s*>@?([^<]*)<\/mention>/gi;

type SelfContext = {
  currentUserId: string;
  currentUserName: string;
};

function namesMatch(label: string, currentUserName: string): boolean {
  const left = label.trim().toLowerCase().replace(/^@/, "");
  const right = currentUserName.trim().toLowerCase();
  return Boolean(left && right && left === right);
}

function isSelfMention(ctx: SelfContext, mentionId: string | undefined, name: string): boolean {
  if (mentionId && isSameUserId(mentionId, ctx.currentUserId)) {
    return true;
  }
  if (!mentionId && namesMatch(name, ctx.currentUserName)) {
    return true;
  }
  return false;
}

function pushSignoffChip(
  parts: React.ReactNode[],
  name: string,
  key: number,
  ctx: SelfContext,
  signoffId?: string,
) {
  const assignee = name.trim();
  const self = isSelfMention(ctx, signoffId, assignee);
  parts.push(
    <span
      key={key}
      className="collaborate-signoff-inline collaborate-signoff-inline--in-body"
      title={`Sign-off: ${assignee}`}
    >
      <span
        className={`collaborate-signoff-chip collaborate-signoff-chip--in-body${self ? " collaborate-signoff-chip--self" : ""}`}
        role="link"
        tabIndex={0}
      >
        <span className="collaborate-signoff-chip__tag">Sign-off</span>
        <span className="collaborate-signoff-chip__assignee">@{assignee}</span>
      </span>
    </span>,
  );
}

function pushPlaybookChip(parts: React.ReactNode[], name: string, key: number) {
  const assignee = name.trim();
  parts.push(
    <span
      key={key}
      className="collaborate-playbook-inline collaborate-playbook-inline--in-body"
      title={`Update playbook: ${assignee}`}
    >
      <span
        className="collaborate-playbook-chip collaborate-playbook-chip--in-body"
        role="link"
        tabIndex={0}
      >
        <span className="collaborate-playbook-chip__tag">Update playbook</span>
        <span className="collaborate-playbook-chip__assignee">@{assignee}</span>
      </span>
    </span>,
  );
}

function pushMentionChip(
  parts: React.ReactNode[],
  name: string,
  key: number,
  ctx: SelfContext,
  mentionId?: string,
) {
  const label = name.trim();
  const self = isSelfMention(ctx, mentionId, label);
  parts.push(
    <span
      key={key}
      className={`collaborate-mention-chip${self ? " collaborate-mention-chip--self" : ""}`}
      role="link"
      tabIndex={0}
      title={label}
    >
      @{label}
    </span>,
  );
}

function pushTextSegment(parts: React.ReactNode[], text: string, key: number) {
  parts.push(parseInlineFormatting(text, `txt-${key}`));
}

function parseTaggedBody(text: string, ctx: SelfContext): React.ReactNode[] | null {
  TOKEN_RE.lastIndex = 0;
  if (!TOKEN_RE.test(text)) {
    return null;
  }
  TOKEN_RE.lastIndex = 0;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > cursor) {
      pushTextSegment(parts, text.slice(cursor, m.index), key++);
    }
    if (m[2] != null) {
      pushSignoffChip(parts, m[2], key++, ctx, m[1] || undefined);
    } else if (m[3] != null) {
      pushPlaybookChip(parts, m[3], key++);
    } else if (m[5] != null) {
      pushMentionChip(parts, m[5], key++, ctx, m[4] || undefined);
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) {
    pushTextSegment(parts, text.slice(cursor), key++);
  }
  return parts;
}

/** Renders body with sign-off chips, @mention chips, and inline `<b>` / `<i>` formatting. */
const MentionBody: React.FC<MentionBodyProps> = ({ text, html, versionNumber }) => {
  const currentUserId = useCollaborateStore((s) => s.currentUserId);
  const currentUserName = useCollaborateStore((s) => s.usersById[s.currentUserId]?.name ?? "");
  const selfCtx = React.useMemo(
    () => ({ currentUserId, currentUserName }),
    [currentUserId, currentUserName],
  );

  const hasVersionNumber = typeof versionNumber === "number" && Number.isFinite(versionNumber);
  const versionBadge = hasVersionNumber ? (
    <span className="collaborate-version-badge">v{versionNumber}</span>
  ) : null;

  if (html && (html.includes("collaborate-mention") || html.includes("collaborate-signoff-mention"))) {
    return (
      <div className="collaborate-message__text">
        <div className="collaborate-message__text-body">
          <span dangerouslySetInnerHTML={{ __html: html }} />
        </div>
        {versionBadge}
      </div>
    );
  }

  const displayText = unwrapIdlessMentionTags(text);
  const tagged = parseTaggedBody(displayText, selfCtx);
  const bodyContent = tagged ?? parseInlineFormatting(displayText, "body");

  return (
    <div className="collaborate-message__text">
      <div className="collaborate-message__text-body">{bodyContent}</div>
      {versionBadge}
    </div>
  );
};

export default MentionBody;
